import type { BackupStatus } from '@bekebi/contracts/api';
import { ErrorState, LoadingState } from '../../components/states';
import { formatBytes } from '../mailboxes/quota';
import { useBackupStatus } from './api';
import { DefinitionList, Section, UnavailableNote } from './components';
import { formatDateTime, formatRelative } from './format';

/**
 * Mail Server backup status (A-M6; docs/integrations/backups.md), from the backup objects the
 * Storage Bucket listing saw. It shows that a backup ARRIVED — when, and how large — never that it
 * is complete or restorable. With no recent bucket listing the state is unknown, never "on
 * schedule" from old information.
 */

export const BACKUP_STATE_TEXT: Record<BackupStatus['state'], string> = {
  on_schedule: 'On schedule',
  attention: 'Attention required',
  unknown: 'Unknown',
};

const STATE_STYLE: Record<BackupStatus['state'], string> = {
  on_schedule: 'border-success-line bg-success-soft text-success',
  attention: 'border-warning-line bg-warning-soft text-warning',
  unknown: 'border-line-strong text-muted',
};

export function BackupStateBadge({ state }: { state: BackupStatus['state'] }) {
  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATE_STYLE[state]}`}
    >
      {BACKUP_STATE_TEXT[state]}
    </span>
  );
}

/** One sentence explaining the state, from the API's values only. */
export function backupStateSentence(status: BackupStatus): string {
  if (status.lastCheckedAt === null) {
    return 'The Storage Bucket has not been read yet, so nothing is known about backups.';
  }
  if (status.checkIsStale) {
    return `The Storage Bucket was last read ${formatRelative(status.lastCheckedAt)}, so the current backup state is not known.`;
  }
  if (status.latestDaily === null) {
    return 'The Storage Bucket was read, but no daily Mail Server backup was found in it.';
  }
  if (status.state === 'attention') {
    return `No daily Mail Server backup has arrived for more than ${status.attentionAfterHours} hours: the newest arrived ${formatRelative(status.latestDaily.uploadedAt)}.`;
  }
  return `The newest daily Mail Server backup arrived ${formatRelative(status.latestDaily.uploadedAt)}.`;
}

export function BackupStatusSection() {
  const query = useBackupStatus();
  return (
    <Section
      title="Backups"
      description="Daily Mail Server backups, as found in the Storage Bucket. This shows that a backup arrived — not that it is complete or can be restored."
    >
      {query.isPending ? (
        <LoadingState label="Loading backup status…" />
      ) : query.isError ? (
        <ErrorState
          title="Backup status could not be loaded"
          message={query.error.message}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <BackupDetails status={query.data} />
      )}
    </Section>
  );
}

function BackupDetails({ status }: { status: BackupStatus }) {
  const known = status.lastCheckedAt !== null && !status.checkIsStale;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <BackupStateBadge state={status.state} />
        <p className="text-sm text-fg">{backupStateSentence(status)}</p>
      </div>
      {status.lastCheckedAt === null ? (
        <UnavailableNote>
          Backup status comes from the Storage Bucket. Once the bucket&apos;s read-only access is
          set up, it is read every hour and each backup&apos;s arrival is recorded.
        </UnavailableNote>
      ) : (
        <>
          <DefinitionList
            items={[
              [
                'Newest daily backup',
                status.latestDaily
                  ? `${status.latestDaily.backupDate} — arrived ${formatDateTime(status.latestDaily.uploadedAt)}, ${formatBytes(status.latestDaily.sizeBytes)}`
                  : 'None found',
              ],
              [
                'Last manual backup',
                status.latestManual
                  ? `${status.latestManual.backupDate} — arrived ${formatDateTime(status.latestManual.uploadedAt)}`
                  : 'None found',
              ],
              ['Storage Bucket last read', formatDateTime(status.lastCheckedAt)],
            ]}
          />
          {known ? <RecentDays days={status.recentDays} /> : null}
        </>
      )}
    </div>
  );
}

function RecentDays({ days }: { days: BackupStatus['recentDays'] }) {
  const missing = days.filter((d) => d.uploadedAt === null).length;
  return (
    <div>
      <p className="text-sm font-medium text-muted">
        Last {days.length} days — {days.length - missing} with a daily backup
        {missing > 0 ? `, ${missing} without` : ''}
      </p>
      <ul className="mt-2 flex flex-wrap gap-1.5" aria-label="Daily backups, recent days">
        {days.map((d) => (
          <li
            key={d.date}
            title={
              d.uploadedAt
                ? `${d.date}: arrived ${formatDateTime(d.uploadedAt)}${d.sizeBytes !== null ? `, ${formatBytes(d.sizeBytes)}` : ''}`
                : `${d.date}: no backup found`
            }
            className={`rounded-md border px-2 py-1 font-mono text-xs ${
              d.uploadedAt ? 'border-success-line text-success' : 'border-warning-line text-warning'
            }`}
          >
            {d.date.slice(5)} {d.uploadedAt ? '✓' : '—'}
          </li>
        ))}
      </ul>
    </div>
  );
}
